// src/lib/partner/partnerProgress.js
import { supabase } from "../../supabaseClient";
import { sanityClient } from "../sanity/sanityClient";

const QUEST_TITLES_QUERY = `
  *[
    _type == "quest" &&
    defined(order) &&
    defined(title)
  ] | order(order asc) {
    order,
    title
  }
`;

let questTitleMapPromise = null;

function requireId(value, label) {
  const cleanValue = String(
    value || ""
  ).trim();

  if (!cleanValue) {
    throw new Error(
      `${label} is required.`
    );
  }

  return cleanValue;
}

function normaliseQuestId(value) {
  const cleanValue = String(
    value ?? ""
  )
    .trim()
    .toLowerCase();

  if (!cleanValue) {
    return "";
  }

  const numberMatch =
    cleanValue.match(/\d+/);

  if (numberMatch) {
    return String(
      Number.parseInt(
        numberMatch[0],
        10
      )
    );
  }

  return cleanValue;
}

async function fetchQuestTitleMap() {
  if (!questTitleMapPromise) {
    questTitleMapPromise =
      sanityClient
        .fetch(
          QUEST_TITLES_QUERY
        )
        .then((rows) => {
          const titleMap =
            new Map();

          const quests =
            Array.isArray(rows)
              ? rows
              : [];

          quests.forEach(
            (quest) => {
              const questId =
                normaliseQuestId(
                  quest?.order
                );

              const questTitle =
                String(
                  quest?.title || ""
                ).trim();

              if (
                questId &&
                questTitle
              ) {
                titleMap.set(
                  questId,
                  questTitle
                );
              }
            }
          );

          return titleMap;
        })
        .catch((error) => {
          /*
           * Clear the cached promise so
           * a later page load can retry.
           */
          questTitleMapPromise =
            null;

          throw error;
        });
  }

  return questTitleMapPromise;
}

export async function fetchPartnerCohortProgress(
  cohortId
) {
  const cleanCohortId =
    requireId(
      cohortId,
      "Cohort ID"
    );

  const { data, error } =
    await supabase.rpc(
      "get_partner_cohort_progress",
      {
        p_cohort_id:
          cleanCohortId,
      }
    );

  if (error) {
    throw error;
  }

  return Array.isArray(data)
    ? data
    : [];
}

export async function fetchPartnerLearnerDetail(
  cohortLearnerId
) {
  const cleanCohortLearnerId =
    requireId(
      cohortLearnerId,
      "Cohort learner ID"
    );

  const { data, error } =
    await supabase.rpc(
      "get_partner_learner_detail",
      {
        p_cohort_learner_id:
          cleanCohortLearnerId,
      }
    );

  if (error) {
    throw error;
  }

  return Array.isArray(data)
    ? data[0] || null
    : null;
}

export async function fetchPartnerLearnerQuestProgress(
  cohortLearnerId
) {
  const cleanCohortLearnerId =
    requireId(
      cohortLearnerId,
      "Cohort learner ID"
    );

  const { data, error } =
    await supabase.rpc(
      "get_partner_learner_quest_progress",
      {
        p_cohort_learner_id:
          cleanCohortLearnerId,
      }
    );

  if (error) {
    throw error;
  }

  const questRows =
    Array.isArray(data)
      ? data
      : [];

  if (questRows.length === 0) {
    return [];
  }

  try {
    const questTitleMap =
      await fetchQuestTitleMap();

    return questRows.map(
      (quest) => {
        const questId =
          normaliseQuestId(
            quest?.quest_id
          );

        return {
          ...quest,

          quest_title:
            questTitleMap.get(
              questId
            ) || null,
        };
      }
    );
  } catch (sanityError) {
    /*
     * Progress should still display
     * even if Sanity is temporarily
     * unavailable.
     */
    console.warn(
      "Partner quest titles could not be loaded from Sanity:",
      sanityError
    );

    return questRows.map(
      (quest) => ({
        ...quest,
        quest_title: null,
      })
    );
  }
}

export async function fetchPartnerLearnerTasks(
  cohortLearnerId
) {
  const cleanCohortLearnerId =
    requireId(
      cohortLearnerId,
      "Cohort learner ID"
    );

  const { data, error } =
    await supabase.rpc(
      "get_partner_learner_tasks",
      {
        p_cohort_learner_id:
          cleanCohortLearnerId,
      }
    );

  if (error) {
    throw error;
  }

  return Array.isArray(data)
    ? data
    : [];
}

export async function fetchPartnerLearnerProfileSummary(
  cohortLearnerId
) {
  if (!cohortLearnerId) {
    throw new Error(
      "The entrepreneur could not be identified."
    );
  }


  const {
    data,
    error,
  } = await supabase.rpc(
    "get_partner_learner_profile_summary",
    {
      p_cohort_learner_id:
        cohortLearnerId,
    }
  );


  if (error) {
    throw error;
  }


  const row =
    Array.isArray(data)
      ? data[0]
      : data;


  if (!row) {
    throw new Error(
      "The entrepreneur could not be found or you do not have permission to view their details."
    );
  }


  return row;
}


export async function fetchPartnerLearnerSubquestProgress(
  cohortLearnerId
) {
  if (!cohortLearnerId) {
    throw new Error(
      "The entrepreneur could not be identified."
    );
  }


  const {
    data,
    error,
  } = await supabase.rpc(
    "get_partner_learner_subquest_progress",
    {
      p_cohort_learner_id:
        cohortLearnerId,
    }
  );


  if (error) {
    throw error;
  }


  return Array.isArray(data)
    ? data
    : [];
}